// Shared design tokens for both themes.
//
// The visual language is a neatly annotated field notebook kept in a field
// office: parchment (light) or loam (black) surfaces, soil-brown ink, and a
// foliage accent used sparingly for headings and hand-drawn outlines.
// Decorative strokes never sit behind text or focus rings, so contrast and
// focus visibility stay intact.
//
// The palette is drawn from the working environment of the people who use it --
// soil, leaf, straw, terracotta, and weathered timber -- rather than from a
// generic product palette. Every foreground/background pair is checked against
// WCAG 2.1 AA in `tokens.test.ts`, so a later "small colour tweak" cannot
// quietly make a reading or a label unreadable outdoors in daylight.
//
// Fonts: a handwritten face for branding and headings, a highly readable
// companion for measurements, tables, inputs, and longer text. Both are
// self-hosted and cover the shipping languages.

/** Font stacks. `--sensing-font-hand` is used only for short accents. */
export const FONTS = {
  hand: "'Caveat Variable', 'Caveat', cursive",
  body: "'Inter Variable', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  numeric: "'Inter Variable', 'Inter', ui-monospace, 'SFMono-Regular', monospace",
} as const;

/**
 * Colour ramps.
 *
 * Each family runs lightest to darkest, which is the order Mantine expects, so a
 * single ramp serves both schemes. The families are named after what they are
 * taken from, which keeps the intent legible when a value is tuned later:
 * `foliage` and `sage` are leaf greens, `moss` is a shaded green, `amber` is
 * straw and grain, `clay` is fired terracotta, `rust` is oxidised iron, and
 * `stone` is weathered timber and dry stone.
 */
export const RAMPS = {
  /** Leaf green. The light-scheme primary; shade 3 clears AA on parchment. */
  foliage: [
    '#eef0e2',
    '#dfe4c9',
    '#c7d1a4',
    '#556b2f',
    '#495c28',
    '#3d4d22',
    '#313e1b',
    '#262f15',
    '#1a210f',
    '#0e1208',
  ],
  /** Dry leaf green. The dark-scheme primary; shade 3 clears AA on loam. */
  sage: [
    '#eaf0d9',
    '#d7e2bd',
    '#c4d39e',
    '#b3c77e',
    '#9fb26a',
    '#8a9b58',
    '#6f7d46',
    '#545f35',
    '#394124',
    '#1e2313',
  ],
  /** Shaded green, used for success and confirmation. */
  moss: [
    '#e7efe2',
    '#d0dfc7',
    '#b2c9a3',
    '#8fae80',
    '#749560',
    '#5e7d4b',
    '#4a643b',
    '#384d2c',
    '#28381f',
    '#182513',
  ],
  /** Straw and grain, used for caution and unconfirmed values. */
  amber: [
    '#fbf2dc',
    '#f5e2b8',
    '#eecd8b',
    '#e5b65c',
    '#d4922a',
    '#b87a1e',
    '#966017',
    '#744a13',
    '#54350e',
    '#332008',
  ],
  /** Fired terracotta, used for role distinction. */
  clay: [
    '#f9ece2',
    '#f1d8c4',
    '#e6bd9f',
    '#d99f76',
    '#c88352',
    '#ab6839',
    '#8c522c',
    '#6d4022',
    '#4f2d18',
    '#32200f',
  ],
  /** Oxidised iron, used for failure and destruction. */
  rust: [
    '#f8e4de',
    '#f0c9be',
    '#e5a896',
    '#d7836a',
    '#c06345',
    '#9c3f27',
    '#80321e',
    '#642616',
    '#481a0f',
    '#2c0f07',
  ],
  /** Wet stone, used for neutral information. Kept warm rather than blue-grey,
   * so a neutral badge does not read as a different design system. */
  stone: [
    '#f0ebe4',
    '#ded6ca',
    '#c4b9a8',
    '#a99d8a',
    '#8d8271',
    '#746a5b',
    '#5c5447',
    '#463f35',
    '#2f2a23',
    '#1a1712',
  ],
  /** Soil-brown text, lightest to darkest. */
  ink: ['#8a8070', '#6b6252', '#4a4234', '#2a2419', '#1c1810'],
  /** Parchment surfaces, lightest to darkest. */
  paper: ['#fffdf7', '#faf6ee', '#f0e9db', '#e4dac6', '#d6c9af'],
  /** Loam surfaces, lightest to darkest. */
  charcoal: ['#3b3529', '#2a251c', '#1f1b15', '#14120e', '#0b0a07'],
  /**
   * Mantine's `dark` ramp in the loam palette, by the role Mantine gives each
   * shade: 0 text, 2 dimmed, 3 placeholder, 4 border, 5 hover, 6 inputs and
   * default buttons, 7 body. Shades 0, 2, 4 and 7 equal the black theme's ink,
   * muted ink, border and surface tokens.
   */
  loam: [
    '#f0ece3',
    '#d6cfc0',
    '#a89f8d',
    '#857c6b',
    '#3b3529',
    '#332d23',
    '#1f1b15',
    '#14120e',
    '#0b0a07',
    '#060504',
  ],
} as const;

export interface ThemeDefinition {
  /** Mantine color scheme identifier. */
  scheme: 'light' | 'dark';
  primaryColor: string;
  colors: Record<string, readonly string[]>;
  /** Semantic tokens applied as CSS custom properties. */
  cssVariables: Record<string, string>;
  /**
   * Mantine's own variables that must be re-pointed at the palette.
   *
   * These cannot be reached through `colors` alone: `--mantine-color-dimmed` is
   * a separate computed value, and leaving it at Mantine's default puts a
   * blue-grey `#868e96` behind `c="dimmed"` in an otherwise earthy interface.
   * It is also the single most-used colour in the app, so it sets the tone of
   * every secondary label, timestamp, and unit.
   */
  mantineVariables: Record<string, string>;
}

/** Light theme: parchment surfaces with soil-brown ink. */
export const lightTheme: ThemeDefinition = {
  scheme: 'light',
  primaryColor: 'foliage',
  colors: {
    foliage: RAMPS.foliage,
    ink: RAMPS.ink,
    paper: RAMPS.paper,
    rust: RAMPS.rust,
  },
  cssVariables: {
    '--sensing-surface': '#faf6ee',
    '--sensing-surface-raised': '#fffdf7',
    '--sensing-surface-sunken': '#f0e9db',
    '--sensing-ink': '#2a2419',
    '--sensing-ink-muted': '#6b6252',
    '--sensing-border': '#ddd3c0',
    '--sensing-border-strong': '#96896e',
    '--sensing-accent': '#556b2f',
    '--sensing-accent-contrast': '#fffdf7',
    // A straw wash behind emphasised values, low enough that the text above it
    // keeps its own contrast rather than relying on the wash for legibility.
    '--sensing-highlight': 'rgba(212, 146, 42, 0.3)',
    '--sensing-danger': '#9c3f27',
    '--sensing-grid': '#e7dfcd',
  },
  mantineVariables: {
    // Secondary text. Matching the semantic muted ink keeps `c="dimmed"` in the
    // same family as the rest of the interface instead of a cool blue-grey.
    '--mantine-color-dimmed': '#6b6252',
  },
};

/** Black theme: loam background, raised timber surfaces, warm off-white ink. */
export const blackTheme: ThemeDefinition = {
  scheme: 'dark',
  primaryColor: 'sage',
  colors: {
    sage: RAMPS.sage,
    ink: [...RAMPS.ink].reverse(),
    charcoal: RAMPS.charcoal,
    rust: [...RAMPS.rust].reverse(),
  },
  cssVariables: {
    '--sensing-surface': '#14120e',
    '--sensing-surface-raised': '#1f1b15',
    '--sensing-surface-sunken': '#0b0a07',
    '--sensing-ink': '#f0ece3',
    '--sensing-ink-muted': '#a89f8d',
    '--sensing-border': '#3b3529',
    '--sensing-border-strong': '#6f6656',
    '--sensing-accent': '#b3c77e',
    '--sensing-accent-contrast': '#14120e',
    '--sensing-highlight': 'rgba(212, 146, 42, 0.26)',
    '--sensing-danger': '#e79079',
    '--sensing-grid': '#2a251c',
  },
  mantineVariables: {
    '--mantine-color-dimmed': '#a89f8d',
  },
};

/** Apply a theme's semantic tokens to the document root. */
export function applyThemeVariables(definition: ThemeDefinition): void {
  const root = document.documentElement;
  for (const [name, value] of Object.entries(definition.cssVariables)) {
    root.style.setProperty(name, value);
  }
  // Inline properties on the root element outrank Mantine's own stylesheet, so
  // these win without needing to match its selectors.
  for (const [name, value] of Object.entries(definition.mantineVariables)) {
    root.style.setProperty(name, value);
  }
  for (const [name, value] of Object.entries(FONTS)) {
    root.style.setProperty(`--sensing-font-${name}`, value);
  }
}
